import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import get from "lodash.get";
import {formatRupees, roundMoney} from "../../utils/pcNonPayable";

// Local style tokens (this branch has no shared uiTokens / chipStyles).
const TEXT = {primary: "#101828", secondary: "#475467", label: "#667085"};
const SURFACE = {page: "#F9FAFB", card: "#FFFFFF"};
const BORDER = {subtle: "#EAECF0"};
const STATUS = {success: "#0B7A47", danger: "#B42318"};
const RADIUS = {sm: "6px", md: "8px"};
const SHADOW = {sm: "0 1px 2px rgba(16, 24, 40, 0.05)"};
const fieldLabelSx = {
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    lineHeight: 1.5,
    color: TEXT.label,
};
const TONES = {
    success: {fg: "#0B7A47", bg: "#ECFDF3", border: "#A6E9C5"},
    warning: {fg: "#B54708", bg: "#FFFAEB", border: "#FEC84B"},
    neutral: {fg: "#475467", bg: "#F2F4F7", border: "#D0D5DD"},
};
const toneStyle = (tone) => {
    const t = TONES[tone] || TONES.neutral;
    return {
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: "5px",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "15px",
        color: t.fg,
        backgroundColor: t.bg,
        border: `1px solid ${t.border}`,
    };
};

// Package construct: when the claim's procedure matches an ANH package, the lines the package covers
// (level L1) are replaced by ONE package line; L2 implants, L3 high-end investigations and taxes stay
// on tariff. The claim can be switched between that package view and its original tariff lines.

const LEVEL_NAMES = {
    L1: "Basic hospitalisation",
    L2: "Implants",
    L3: "High-end investigation",
    TAX: "Tax",
};

/** Package information off the tariff response, or null for a claim without a package. */
export const readPackageInfo = (tariffResponseData) => {
    if (!get(tariffResponseData, ['package_applied'], false)) {
        return null;
    }
    return {
        packageViewActive: get(tariffResponseData, ['package_view_active'], true) !== false,
        summary: get(tariffResponseData, ['package_summary'], null) || {},
        breakdown: get(tariffResponseData, ['package_breakdown_line_items'], []) || [],
        updatedBy: get(tariffResponseData, ['package_view_updated_by'], null),
        updatedAt: get(tariffResponseData, ['package_view_updated_at'], null),
    };
};

const levelLabel = (level) => (level ? `${level} · ${LEVEL_NAMES[level] || level}` : "");

/** Badge for the Bill Line Item cell. Renders nothing for a line without package information. */
export function PackageLineBadge({row}) {
    if (!row || (row.is_package_line == null && row.package_level == null)) {
        return null;
    }
    let tone;
    let text;
    if (row.is_package_line) {
        tone = "success";
        text = "Package";
    } else if (row.included_in_package) {
        tone = "neutral";
        text = `In package · ${row.package_level}`;
    } else {
        tone = "warning";
        text = `Excluded · ${row.package_level}`;
    }
    return (
        <Tooltip title={row.package_construct_remarks || ""} arrow>
            <span style={{display: "block", marginTop: 4}}>
                <span style={{...toneStyle(tone), width: "auto", whiteSpace: "nowrap"}}>{text}</span>
            </span>
        </Tooltip>
    );
}

const figureSx = {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 130,
};

function Figure({label, value, color, hint}) {
    return (
        <Tooltip title={hint || ""} arrow disableHoverListener={!hint}>
            <Box sx={figureSx}>
                <Typography component="span" sx={fieldLabelSx}>{label}</Typography>
                <Typography component="span" sx={{fontSize: "14px", fontWeight: 700, color: color || TEXT.primary}}>
                    {value}
                </Typography>
            </Box>
        </Tooltip>
    );
}

const amountCellSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"};

function LinesTable({title, caption, lines, showShare}) {
    const billed = lines.reduce((sum, line) => sum + roundMoney(line.total_bill_amount), 0);
    const tariff = lines.reduce((sum, line) => sum + roundMoney(line.insurerAmount ?? line.admissible_amount), 0);
    const share = lines.reduce((sum, line) => sum + roundMoney(line.package_allocated_amount), 0);
    return (
        <Box sx={{mb: 3}}>
            <Typography sx={{fontSize: "14px", fontWeight: 700, color: TEXT.primary}}>
                {title} <span style={{color: TEXT.label, fontWeight: 500}}>({lines.length})</span>
            </Typography>
            <Typography sx={{fontSize: "12px", color: TEXT.secondary, mb: 1}}>{caption}</Typography>
            <TableContainer sx={{border: `1px solid ${BORDER.subtle}`, borderRadius: RADIUS.md}}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{backgroundColor: SURFACE.page}}>
                            <TableCell>Row</TableCell>
                            <TableCell>Bill Line Item</TableCell>
                            <TableCell>Master Category</TableCell>
                            <TableCell>Level</TableCell>
                            <TableCell align="right">Billed</TableCell>
                            <TableCell align="right">At Tariff</TableCell>
                            {showShare && <TableCell align="right">Package Share</TableCell>}
                            <TableCell>Remarks</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lines.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={showShare ? 8 : 7} sx={{color: TEXT.label, textAlign: "center"}}>
                                    No lines
                                </TableCell>
                            </TableRow>
                        )}
                        {lines.map((line) => (
                            <TableRow key={line.row_id}>
                                <TableCell>{line.row_id}</TableCell>
                                <TableCell>{line.bill_line_item}</TableCell>
                                <TableCell>{line.master_category}</TableCell>
                                <TableCell sx={{whiteSpace: "nowrap"}}>{levelLabel(line.package_level)}</TableCell>
                                <TableCell align="right" sx={amountCellSx}>{formatRupees(line.total_bill_amount)}</TableCell>
                                <TableCell align="right" sx={amountCellSx}>
                                    {formatRupees(line.insurerAmount ?? line.admissible_amount)}
                                </TableCell>
                                {showShare && (
                                    <TableCell align="right" sx={amountCellSx}>{formatRupees(line.package_allocated_amount)}</TableCell>
                                )}
                                <TableCell sx={{fontSize: "12px", color: TEXT.secondary, minWidth: 260}}>
                                    {line.package_construct_remarks}
                                </TableCell>
                            </TableRow>
                        ))}
                        {lines.length > 0 && (
                            <TableRow sx={{backgroundColor: SURFACE.page}}>
                                <TableCell colSpan={4} sx={{fontWeight: 700}}>Total</TableCell>
                                <TableCell align="right" sx={{...amountCellSx, fontWeight: 700}}>{formatRupees(billed)}</TableCell>
                                <TableCell align="right" sx={{...amountCellSx, fontWeight: 700}}>{formatRupees(tariff)}</TableCell>
                                {showShare && (
                                    <TableCell align="right" sx={{...amountCellSx, fontWeight: 700}}>{formatRupees(share)}</TableCell>
                                )}
                                <TableCell/>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function PackageLinesDialog({open, onClose, packageInfo}) {
    const summary = packageInfo.summary || {};
    const included = packageInfo.breakdown.filter((line) => line.included_in_package);
    const excluded = packageInfo.breakdown.filter((line) => !line.included_in_package && line.package_level);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{pb: 0.5}}>
                Package lines — {summary.package_name || "Package"}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: "12.5px", color: TEXT.secondary, mb: 2}}>
                    Package rate {formatRupees(summary.package_amount ?? summary.package_rate)}
                    {summary.room_column ? ` (${summary.room_column})` : ""}
                    {summary.matched_text ? ` · matched on "${summary.matched_text}"` : ""}
                    {summary.page_number ? ` · ANH page ${summary.page_number}` : ""}
                </Typography>
                <LinesTable
                    title="Included in package"
                    caption="Replaced by the package line on the package view; the package share is each line's part of the package amount."
                    lines={included}
                    showShare
                />
                <LinesTable
                    title="Excluded from package"
                    caption="Implants (L2), high-end investigations (L3) and taxes — paid at tariff on both views."
                    lines={excluded}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

const toggleSx = {
    backgroundColor: SURFACE.page,
    border: `1px solid ${BORDER.subtle}`,
    borderRadius: RADIUS.md,
    p: "3px",
    "& .MuiToggleButtonGroup-grouped": {
        border: 0,
        borderRadius: `${RADIUS.sm} !important`,
        textTransform: "none",
        fontSize: "12.5px",
        fontWeight: 600,
        lineHeight: 1.4,
        px: 1.75,
        py: 0.5,
        color: TEXT.secondary,
        "&:hover": {backgroundColor: "transparent", color: TEXT.primary},
        "&.Mui-selected": {
            backgroundColor: SURFACE.card,
            color: TEXT.primary,
            boxShadow: SHADOW.sm,
            "&:hover": {backgroundColor: SURFACE.card},
        },
    },
};

/**
 * Package summary, the Package / Original tariff switch and the included / excluded lines window.
 * `onSwitch(packageView)` performs the switch; `hasStagedEdits` says unsaved grid edits would be lost.
 */
export function PackageConstructBar({packageInfo, onSwitch, switching, hasStagedEdits, canSwitch}) {
    const [confirmView, setConfirmView] = useState(null);
    const [linesOpen, setLinesOpen] = useState(false);
    const summary = packageInfo.summary || {};
    const viewValue = packageInfo.packageViewActive ? "package" : "tariff";
    const includedCount = useMemo(
        () => packageInfo.breakdown.filter((line) => line.included_in_package).length,
        [packageInfo.breakdown]
    );
    const savingsVsTariff = summary.savings_vs_tariff;

    const handleChange = (event, value) => {
        if (value === null || value === viewValue) {
            return;
        }
        setConfirmView(value);
    };

    const confirmSwitch = () => {
        const packageView = confirmView === "package";
        setConfirmView(null);
        onSwitch(packageView);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 3,
                mx: "10px",
                mb: "10px",
                px: 2,
                py: 1.5,
                backgroundColor: SURFACE.card,
                border: `1px solid ${BORDER.subtle}`,
                borderLeft: `4px solid ${STATUS.success}`,
                borderRadius: RADIUS.md,
                boxShadow: SHADOW.sm,
            }}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
                <Typography component="span" sx={fieldLabelSx}>Tariff lines</Typography>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewValue}
                        onChange={handleChange}
                        disabled={!canSwitch || switching}
                        aria-label="Tariff lines"
                        sx={toggleSx}
                    >
                        <ToggleButton value="package">Package applied</ToggleButton>
                        <ToggleButton value="tariff">Original tariff</ToggleButton>
                    </ToggleButtonGroup>
                    {switching && <CircularProgress size={18}/>}
                    <Button variant="outlined" size="small" onClick={() => setLinesOpen(true)}
                            sx={{textTransform: "none", fontWeight: 600, whiteSpace: "nowrap"}}>
                        Included / Excluded lines
                    </Button>
                </Box>
                {packageInfo.updatedBy && (
                    <Typography sx={{fontSize: "11px", color: TEXT.label}}>
                        Switched by {packageInfo.updatedBy}
                        {packageInfo.updatedAt ? ` · ${String(packageInfo.updatedAt).replace("T", " ").slice(0, 16)}` : ""}
                    </Typography>
                )}
            </Box>
            <Figure
                label="Package"
                value={summary.package_name || "—"}
                hint={summary.room_column ? `Rate for ${summary.room_column}` : ""}
            />
            <Figure label="Package amount" value={formatRupees(summary.package_amount ?? summary.package_rate)}/>
            <Figure
                label="Lines in package"
                value={`${includedCount} lines · ${formatRupees(summary.included_billed_amount)}`}
                hint="Billed amount of the lines the package replaces"
            />
            <Figure
                label="Saved vs billed"
                value={formatRupees(summary.savings_vs_billed)}
                color={STATUS.success}
                hint="Billed amount of the included lines minus the package amount"
            />
            {savingsVsTariff != null && (
                <Figure
                    label="Saved vs original tariff"
                    value={formatRupees(savingsVsTariff)}
                    color={roundMoney(savingsVsTariff) < 0 ? STATUS.danger : STATUS.success}
                    hint="Original tariff total minus package total, when tariff was applied. Negative: the package pays more."
                />
            )}

            <PackageLinesDialog open={linesOpen} onClose={() => setLinesOpen(false)} packageInfo={packageInfo}/>

            <Dialog open={confirmView !== null} onClose={() => setConfirmView(null)} maxWidth="xs">
                <DialogTitle>
                    Switch to {confirmView === "package" ? "package applied" : "original tariff"} lines?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{fontSize: "13.5px"}}>
                        {confirmView === "package"
                            ? "The lines covered by the package are replaced by one package line. "
                            : "Every bill line is shown and paid at its tariff rate; the package line is removed. "}
                        The claim amount after tariff is updated and PML is re-run.
                        Line edits stay with the view they were saved on.
                        {hasStagedEdits && (
                            <b style={{display: "block", marginTop: 8, color: STATUS.danger}}>
                                Unsaved tariff edits on this view will be discarded.
                            </b>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmView(null)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmSwitch}>Switch</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
